import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon } from 'lucide-react';
export function PriceAlert() {
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  useEffect(() => {
    // Get or set the deadline (tomorrow at midnight)
    const getDeadline = () => {
      const savedDeadline = document.cookie.split('; ').find(row => row.startsWith('priceDeadline='))?.split('=')[1];
      if (savedDeadline) {
        return parseInt(savedDeadline, 10);
      }
      // Set deadline to tomorrow at midnight
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const deadline = tomorrow.getTime();
      // Save to cookie (expires in 7 days)
      const expires = new Date();
      expires.setDate(expires.getDate() + 7);
      document.cookie = `priceDeadline=${deadline}; expires=${expires.toUTCString()}; path=/`;
      return deadline;
    };
    const deadline = getDeadline();
    const updateTimer = () => {
      const now = new Date().getTime();
      const distance = deadline - now;
      if (distance <= 0) {
        setIsExpired(true);
        setTimeLeft({
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        // Save expired state to cookie
        const expires = new Date();
        expires.setDate(expires.getDate() + 7);
        document.cookie = `priceExpired=true; expires=${expires.toUTCString()}; path=/`;
        return;
      }
      const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(distance % (1000 * 60) / 1000);
      setTimeLeft({
        hours,
        minutes,
        seconds
      });
    };
    // Check if already expired from cookie
    const isAlreadyExpired = document.cookie.split('; ').find(row => row.startsWith('priceExpired='))?.split('=')[1] === 'true';
    if (isAlreadyExpired) {
      setIsExpired(true);
      setTimeLeft({
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    } else {
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, []);
  const formatDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  if (!timeLeft) return null;
  return <motion.div initial={{
    y: -100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className="sticky top-0 z-50 w-full overflow-hidden" data-id="element-263">
      {/* Main Banner */}
      <div className="relative bg-gradient-to-r from-gold-dark via-gold to-gold-light shadow-2xl" data-id="element-264">
        {/* Animated shine effect */}
        <motion.div animate={{
        x: ['-100%', '200%']
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear',
        repeatDelay: 1
      }} className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" data-id="element-265" />

        {/* Content Container */}
        <div className="relative py-3 md:py-4 px-4" data-id="element-266">
          <div className="max-w-7xl mx-auto" data-id="element-267">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-6" data-id="element-268">
              {/* Left: Alert Icon + Message */}
              <div className="flex items-center gap-2 md:gap-3 flex-wrap" data-id="element-269">
                <motion.div animate={{
                scale: [1, 1.15, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="flex-shrink-0" data-id="element-270">
                  <div className="relative" data-id="element-271">
                    <div className="absolute inset-0 bg-navy/20 rounded-full blur-md" data-id="element-272" />
                    <AlertCircleIcon className="relative h-5 w-5 md:h-6 md:w-6 text-navy" aria-hidden="true" data-id="element-273" />
                  </div>
                </motion.div>

                <div className="flex-1 min-w-0" data-id="element-274">
                  <p className="font-heading font-black text-sm md:text-lg text-navy leading-tight" data-id="element-275">
                    {isExpired ? 'OFERTA EXPIRADA' : <>
                        <span className="hidden md:inline" data-id="element-276">⚡ </span>
                        PRECIO DE LANZAMIENTO:{' '}
                        <span className="text-navy-dark" data-id="element-277">$37</span>
                      </>}
                  </p>
                  <p className="font-body text-xs md:text-sm text-navy-dark font-semibold mt-0.5 flex items-center gap-2 flex-wrap" data-id="element-278">
                    {isExpired ? 'Precio actual: $97' : <>
                        <span data-id="element-279">Sube a $97 el {formatDate()}</span>
                        {!isExpired && <span className="inline-flex items-center gap-1 bg-navy-dark px-2 py-0.5 rounded text-gold" data-id="element-280">
                            <span className="font-heading font-black text-sm tabular-nums" data-id="element-281">
                              {String(timeLeft.hours).padStart(2, '0')}:
                              {String(timeLeft.minutes).padStart(2, '0')}:
                              {String(timeLeft.seconds).padStart(2, '0')}
                            </span>
                          </span>}
                      </>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative border */}
        <div className="h-1 bg-gradient-to-r from-navy-dark via-navy to-navy-dark" data-id="element-282" />
      </div>
    </motion.div>;
}